import IMediaItem from '@inter-ikea-kompis/component-media-carousel/lib/types/IMediaItem';
import { IVpcConfiguration } from '@inter-ikea-kompis/types';
import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import { ICFArticle } from 'ICF/interfaces';
import { ICatalog } from 'range/interface/catalog';
import { isShallowIdentical } from 'utils/isShallowIdentical';
import { StorageBehindApplicationsEnum } from 'utils/storageBehind';

export interface IPanelStyle {
  index: number;
  isFlipped: boolean;
}

export interface IPanelPatternStyles {
  primaryPanelStyle: IPanelStyle;
  secondaryPanelStyle?: IPanelStyle;
}

export interface IStorageBehindVpcConfiguration extends IVpcConfiguration {
  application: StorageBehindApplicationsEnum;
  icfArticles: ICFArticle[];
}
export type Configuration = {
  showAppLoadingScreen: boolean;
  defaultPanelStyles: IPanelStyle[];
  selectedPanelStyles: IPanelStyle[][];
  selectedPanelPatternStyles: IPanelPatternStyles;
  selectedDoor?: number;
  storageBehindVisible: boolean;
  storageBehind: IStorageBehindVpcConfiguration | null;
  unsupportedStorageBehind: IStorageBehindVpcConfiguration | null;
  activeStorageBehindGallery: StorageBehindApplicationsEnum | null;
  skyttaCatalogData: ICatalog | null;
  paxPanelsCatalogData: ICatalog | null;
  showErrorPage: boolean;
  showLoadingSummaryButton: boolean;
  showSceneLoadingScreen: boolean;
  hasUnsavedChanges: boolean;
  screenshot: IMediaItem | null;
  arrivedFromQrCode: boolean;
  readyToSaveCoreState: boolean;
  sceneReady: boolean;
};

const initialState: Configuration = {
  showAppLoadingScreen: true,
  defaultPanelStyles: [],
  selectedPanelStyles: [[{ index: 0, isFlipped: false }]],
  selectedPanelPatternStyles: { primaryPanelStyle: { index: 0, isFlipped: false } },
  selectedDoor: undefined,
  storageBehindVisible: false,
  storageBehind: null,
  unsupportedStorageBehind: null,
  activeStorageBehindGallery: null,
  skyttaCatalogData: null,
  paxPanelsCatalogData: null,
  showErrorPage: false,
  showLoadingSummaryButton: false,
  showSceneLoadingScreen: false,
  hasUnsavedChanges: false,
  screenshot: null,
  arrivedFromQrCode: false,
  readyToSaveCoreState: false,
  sceneReady: false
};

export const { actions: configurationAction, reducer: configurationReducer } = createSlice({
  name: 'configuration',
  initialState,
  reducers: {
    setShowAppLoadingScreen: (
      state,
      { payload: showAppLoadingScreen }: PayloadAction<Configuration['showAppLoadingScreen']>
    ) => {
      if (isShallowIdentical(current(state).showAppLoadingScreen, showAppLoadingScreen)) {
        return;
      }

      return { ...state, showAppLoadingScreen };
    },
    setDefaultPanelStyles: (
      state,
      { payload: defaultPanelStyles }: PayloadAction<Configuration['defaultPanelStyles']>
    ) => {
      if (isShallowIdentical(current(state).defaultPanelStyles, defaultPanelStyles)) {
        return;
      }

      return { ...state, defaultPanelStyles };
    },
    setSelectedPanelStyles: (
      state,
      { payload: selectedPanelStyles }: PayloadAction<Configuration['selectedPanelStyles']>
    ) => {
      if (!selectedPanelStyles.length) {
        return;
      }

      if (isShallowIdentical(current(state).selectedPanelStyles, selectedPanelStyles)) {
        return;
      }

      return { ...state, selectedPanelStyles };
    },
    setSelectedPanelPatternStyles: (
      state,
      {
        payload: selectedPanelPatternStyles
      }: PayloadAction<Configuration['selectedPanelPatternStyles']>
    ) => {
      if (
        isShallowIdentical(current(state).selectedPanelPatternStyles, selectedPanelPatternStyles)
      ) {
        return;
      }

      return { ...state, selectedPanelPatternStyles };
    },
    setSelectedDoor: (
      state,
      { payload: selectedDoor }: PayloadAction<Configuration['selectedDoor']>
    ) => {
      if (isShallowIdentical(current(state).selectedDoor, selectedDoor)) {
        return;
      }

      return { ...state, selectedDoor };
    },
    setStorageBehindVisible: (
      state,
      { payload: storageBehindVisible }: PayloadAction<Configuration['storageBehindVisible']>
    ) => {
      if (isShallowIdentical(current(state).storageBehindVisible, storageBehindVisible)) {
        return;
      }

      return { ...state, storageBehindVisible };
    },
    setStorageBehind: (
      state,
      { payload: storageBehind }: PayloadAction<Configuration['storageBehind']>
    ) => {
      if (isShallowIdentical(current(state).storageBehind, storageBehind)) {
        return;
      }

      return { ...state, storageBehind };
    },
    setUnsupportedStorageBehind: (
      state,
      {
        payload: unsupportedStorageBehind
      }: PayloadAction<Configuration['unsupportedStorageBehind']>
    ) => {
      if (isShallowIdentical(current(state).unsupportedStorageBehind, unsupportedStorageBehind)) {
        return;
      }

      return { ...state, unsupportedStorageBehind };
    },
    setActiveStorageBehindGallery: (
      state,
      {
        payload: activeStorageBehindGallery
      }: PayloadAction<Configuration['activeStorageBehindGallery']>
    ) => {
      if (
        isShallowIdentical(current(state).activeStorageBehindGallery, activeStorageBehindGallery)
      ) {
        return;
      }

      return { ...state, activeStorageBehindGallery };
    },
    setSkyttaCatalogData: (
      state,
      { payload: skyttaCatalogData }: PayloadAction<Configuration['skyttaCatalogData']>
    ) => {
      if (isShallowIdentical(current(state).skyttaCatalogData, skyttaCatalogData)) {
        return;
      }

      return { ...state, skyttaCatalogData };
    },
    setPaxPanelsCatalogData: (
      state,
      { payload: paxPanelsCatalogData }: PayloadAction<Configuration['paxPanelsCatalogData']>
    ) => {
      if (isShallowIdentical(current(state).paxPanelsCatalogData, paxPanelsCatalogData)) {
        return;
      }

      return { ...state, paxPanelsCatalogData };
    },
    setShowErrorPage: (
      state,
      { payload: showErrorPage }: PayloadAction<Configuration['showErrorPage']>
    ) => {
      if (isShallowIdentical(current(state).showErrorPage, showErrorPage)) {
        return;
      }

      return { ...state, showErrorPage };
    },
    setShowLoadingSummaryButton: (
      state,
      {
        payload: showLoadingSummaryButton
      }: PayloadAction<Configuration['showLoadingSummaryButton']>
    ) => {
      if (isShallowIdentical(current(state).showLoadingSummaryButton, showLoadingSummaryButton)) {
        return;
      }

      return { ...state, showLoadingSummaryButton };
    },
    setShowLoadingSceneScreen: (
      state,
      { payload: showSceneLoadingScreen }: PayloadAction<Configuration['showSceneLoadingScreen']>
    ) => {
      if (isShallowIdentical(current(state).showSceneLoadingScreen, showSceneLoadingScreen)) {
        return;
      }

      return { ...state, showSceneLoadingScreen };
    },
    setHasUnsavedChanges: (
      state,
      { payload: hasUnsavedChanges }: PayloadAction<Configuration['hasUnsavedChanges']>
    ) => {
      if (isShallowIdentical(current(state).hasUnsavedChanges, hasUnsavedChanges)) {
        return;
      }

      return { ...state, hasUnsavedChanges };
    },
    setScreenshot: (state, { payload: screenshot }: PayloadAction<Configuration['screenshot']>) => {
      if (isShallowIdentical(current(state).screenshot, screenshot)) {
        return;
      }

      return { ...state, screenshot };
    },
    setArrivedFromQrCode: (
      state,
      { payload: arrivedFromQrCode }: PayloadAction<Configuration['arrivedFromQrCode']>
    ) => {
      if (isShallowIdentical(current(state).arrivedFromQrCode, arrivedFromQrCode)) {
        return;
      }

      return { ...state, arrivedFromQrCode };
    },
    setReadyToSaveCoreState: (
      state,
      { payload: readyToSaveCoreState }: PayloadAction<Configuration['readyToSaveCoreState']>
    ) => {
      if (isShallowIdentical(current(state).readyToSaveCoreState, readyToSaveCoreState)) {
        return;
      }

      return { ...state, readyToSaveCoreState };
    },
    setSceneReady: (state, { payload: sceneReady }: PayloadAction<Configuration['sceneReady']>) => {
      if (isShallowIdentical(current(state).sceneReady, sceneReady)) {
        return;
      }

      return { ...state, sceneReady };
    }
  }
});
